// Suggesting addresses as they type, matched in the browser against the platform's addresses, already fetched:
// no request per keystroke. Only input shaped like an address (a house number and the start of a street, or a street
// name and its type) that also starts one of the addresses ever suggests anything.

// Every word that ends a street's name, in full and short ("avenue", "ave"): what a typed address is read against.
const STREET_TYPE_WORDS = [
  "alley",
  "approach",
  "arcade",
  "avenue",
  "ave",
  "boulevard",
  "blvd",
  "boulevarde",
  "bypass",
  "causeway",
  "chase",
  "circle",
  "circuit",
  "cct",
  "close",
  "cl",
  "common",
  "copse",
  "corner",
  "corso",
  "court",
  "ct",
  "crescent",
  "cres",
  "cul-de-sac",
  "drive",
  "dr",
  "entrance",
  "esplanade",
  "esp",
  "fairway",
  "gardens",
  "gate",
  "glade",
  "glen",
  "grove",
  "gr",
  "heights",
  "highway",
  "hwy",
  "kingsway",
  "lane",
  "ln",
  "loop",
  "mall",
  "mews",
  "outlook",
  "parade",
  "pde",
  "parkway",
  "pass",
  "place",
  "pl",
  "promenade",
  "quay",
  "ridge",
  "rise",
  "road",
  "rd",
  "row",
  "square",
  "street",
  "st",
  "terrace",
  "tce",
  "trail",
  "vale",
  "view",
  "vista",
  "walk",
  "way",
  "wharf",
];

const SHORT_FORMS = {
  st: "street",
  rd: "road",
  ave: "avenue",
  av: "avenue",
  dr: "drive",
  ct: "court",
  cres: "crescent",
  pl: "place",
  pde: "parade",
  hwy: "highway",
  ln: "lane",
  tce: "terrace",
  blvd: "boulevard",
  cl: "close",
  cct: "circuit",
  esp: "esplanade",
};
// Every street type, in full and short.
const TYPE_STARTS = [
  ...new Set([
    ...STREET_TYPE_WORDS,
    ...Object.keys(SHORT_FORMS),
    ...Object.values(SHORT_FORMS),
  ]),
];
const STREET_TYPES = new Set(TYPE_STARTS);
// Words that follow a number in ordinary writing ("4 and 3", "10 percent"), never the start of a street.
const JOINING_WORDS = new Set([
  "and",
  "for",
  "per",
  "bed",
  "beds",
  "bath",
  "baths",
  "car",
  "cars",
  "day",
  "days",
  "week",
  "weeks",
  "percent",
]);
const MOST_SUGGESTED = 6;
const LONGEST_WINDOW = 5;

const isNumber = (word) => /^\d/.test(word);

// An address's words as they compare: lowercase, punctuation gone, "st" as "street", a unit "5/12" also as 5 and 12.
const wordsOf = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9/\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((word) =>
      word.includes("/")
        ? [word, ...word.split("/").filter(Boolean)]
        : [SHORT_FORMS[word] || word],
    );

// Typed words are kept as typed, because "pl" may be the start of Plasto as much as short for place: a typed word
// matches an address word it starts, or the full word it is short for.
const typedWords = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9/\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((word) =>
      word.includes("/")
        ? [
            { word },
            ...word
              .split("/")
              .filter(Boolean)
              .map((part) => ({ word: part })),
          ]
        : [{ word, short: SHORT_FORMS[word] }],
    );

const matches = (typed, addressWord) =>
  addressWord.startsWith(typed.word) ||
  Boolean(typed.short && addressWord.startsWith(typed.short));

const push = (map, key, row) => {
  if (map.has(key)) map.get(key).push(row);
  else map.set(key, [row]);
};

// The addresses, ready to match, with two lookup tables so a keystroke only looks at the few that could match
// rather than all of them: by house number (any of the first four words that starts with a digit), and by the first
// three letters of a word.
export const indexAddresses = (properties = []) => {
  const rows = properties
    .filter((property) => property?.address)
    // `order` is the place in the list given, which is most recently updated first.
    .map((property, order) => ({
      ...property,
      order,
      words: wordsOf(property.address),
    }));
  const byNumber = new Map();
  const byStart = new Map();
  for (const row of rows) {
    const starts = new Set();
    row.words.forEach((word, position) => {
      if (isNumber(word)) {
        if (position < 4) push(byNumber, word, row);
      } else if (word.length >= 3 && !starts.has(word.slice(0, 3))) {
        starts.add(word.slice(0, 3));
        push(byStart, word.slice(0, 3), row);
      }
    });
  }
  return { rows, byNumber, byStart, numbers: [...byNumber.keys()].sort() };
};

// The addresses a run of typed words could still become: those with a house number that starts with the typed
// number (they sit together in the sorted numbers), or a word that starts with the typed word's first three letters.
const candidatesFor = (index, first) => {
  if (!isNumber(first)) return index.byStart.get(first.slice(0, 3)) || [];
  let low = 0;
  let high = index.numbers.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (index.numbers[middle] < first) low = middle + 1;
    else high = middle;
  }
  const rows = new Set();
  for (
    ;
    low < index.numbers.length && index.numbers[low].startsWith(first);
    low += 1
  )
    index.byNumber.get(index.numbers[low]).forEach((row) => rows.add(row));
  return [...rows];
};

// Each typed word must start a word of the address, in order. After a house number the street name follows straight
// away, so a second word never skips to the suburb.
const startsAddress = (typed, words) => {
  const isNumbered = isNumber(typed[0].word);
  let at = 0;
  return typed.every((one, position) => {
    while (at < words.length && !matches(one, words[at])) {
      if (isNumbered && position === 1) return false;
      at += 1;
    }
    at += 1;
    return at <= words.length;
  });
};

// Whether a run of typed words is shaped like an address being typed: a house number and at least two letters of
// the street, or a street name of four letters or more with its type (short or whole) after it. A lone word, however
// much it looks like a suburb or street, is just a word.
// `spoken` is what was typed, a word each ("8/38-42" is one).
const looksLikeAddress = (spoken) => {
  const [first, second] = spoken.map((word) =>
    word.toLowerCase().replace(/[^a-z0-9/]/g, ""),
  );
  if (!second) return false;
  // After a house number, three letters of the street (two for "St" as in St Georges), but not a word like "and".
  if (isNumber(first))
    return (
      (second.length >= 3 || second === "st") && !JOINING_WORDS.has(second)
    );
  return (
    first.length >= 4 &&
    !STREET_TYPES.has(first) &&
    second.length >= 2 &&
    TYPE_STARTS.some((type) => type.startsWith(second))
  );
};

// Up to six of the addresses the end of `text` is starting to spell, each with where in `text` the words that spell
// it begin, so a pick can replace them.
export const suggestAddresses = (index, text) => {
  const spoken = [...String(text ?? "").matchAll(/[^\s,]+/g)].slice(
    -LONGEST_WINDOW,
  );
  for (let size = spoken.length; size >= 2; size -= 1) {
    const window = spoken.slice(-size);
    const typed = typedWords(window.map((one) => one[0]).join(" "));
    if (!looksLikeAddress(window.map((one) => one[0]))) continue;
    const found = candidatesFor(index, typed[0].word).filter((property) =>
      startsAddress(typed, property.words),
    );
    // A house number and street typed that is none of the addresses is not an address on the platform: nothing is
    // suggested, and no shorter run of the same words is tried.
    if (!found.length && isNumber(typed[0].word)) return [];
    if (!found.length) continue;
    const written = wordsOf(window.map((one) => one[0]).join(" "));
    const isFullyTyped = (property) =>
      written.length >= property.words.length &&
      written.slice(-property.words.length).join(" ") ===
        property.words.join(" ");
    // An address written out in full is finished: nothing more is suggested.
    if (found.some(isFullyTyped)) return [];
    return found
      .sort(
        (a, b) =>
          Number(b.words[0] === typed[0].word) -
            Number(a.words[0] === typed[0].word) ||
          Number(matches(typed[0], b.words[0])) -
            Number(matches(typed[0], a.words[0])) ||
          // The same address can be held more than once (an earlier campaign, a development and its apartment):
          // each record is its own suggestion, the settled ones after the rest.
          Number(Boolean(a.isSettled)) - Number(Boolean(b.isSettled)) ||
          a.words.length - b.words.length ||
          a.order - b.order,
      )
      .slice(0, MOST_SUGGESTED)
      .map((property) => ({
        propertyId: property.propertyId,
        address: property.address,
        isSettled: Boolean(property.isSettled),
        isMaster: Boolean(property.isMaster),
        replaceFrom: window[0].index,
      }));
  }
  return [];
};

// The text with the words that were being typed replaced by the picked address.
export const withAddress = (text, suggestion) =>
  `${String(text).slice(0, suggestion.replaceFrom)}${suggestion.address} `;

export const applyAddressSuggestion = (text, matchedText, suggestion) => {
  const current = String(text || "");
  const matched = String(matchedText || "");
  if (!current || !matched) return suggestion;
  const at = current.toLowerCase().lastIndexOf(matched.toLowerCase());
  if (at === -1) return suggestion;
  return `${current.slice(0, at)}${suggestion}${current.slice(at + matched.length)}`;
};
